/**
 * Keep the Convex client's credential current for a session that is already
 * authorized.
 *
 * A Firebase ID token lives one hour. `proceedToLobby` installs one on the
 * Convex client at sign-in and nothing replaced it, because `firebase-loader`
 * listened only to `onAuthStateChanged` — which does not fire on a refresh. An
 * hour into any session every client-side Convex read went out unauthenticated,
 * and Convex answers an unauthenticated `foundry:getMyRecipes` with `[]` rather
 * than an error. The Collection published that non-answer as fact and reported
 * every Moon Foundry card as undiscovered.
 *
 * REFRESH, NEVER GRANT. Deciding that a Firebase identity may talk to Convex is
 * `proceedToLobby`'s job, and it happens after the consent gate and the
 * identity checks. This only ever replaces a live credential with a newer one
 * for the SAME subject, so a restored or switched identity can never
 * authenticate itself here by arriving on a refresh.
 */

/** The `sub` claim, read without verifying — only ever compared to itself. */
function tokenSubject(token) {
    const payload = String(token || '').split('.')[1];
    if (!payload) return null;
    try {
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        const claims = JSON.parse(json);
        return typeof claims?.sub === 'string' && claims.sub ? claims.sub : null;
    } catch (_) {
        return null;
    }
}

/**
 * @returns {() => void} unsubscribe
 */
function installConvexTokenRefresh(firebase, cloud) {
    if (typeof firebase?.subscribeIdToken !== 'function') return () => {};
    if (typeof cloud?.setAuthToken !== 'function') return () => {};
    if (typeof cloud?.getAuthToken !== 'function') return () => {};

    return firebase.subscribeIdToken((token) => {
        const live = cloud.getAuthToken();
        // No live session: granting one is not this module's decision.
        if (!live) return;
        // A sign-out clears the credential through its own path, which also
        // tears down the surfaces that were using it.
        if (!token) return;
        if (token === live) return;
        const subject = tokenSubject(token);
        if (!subject || subject !== tokenSubject(live)) return;
        cloud.setAuthToken(token);
    });
}

export { installConvexTokenRefresh };
